import { createContext, useContext, useMemo } from 'react'
import { decodeText } from '@aytm/app/stats/lib/helpers'

import {
  CompareItemsItem,
  CompareItemsLayout,
  CompareItemsTable,
  CompareItemsProps,
  CompareItemsModes,
  CompareItemsSelectedItems,
  CompareItemsImages,
  CompareItemsSigtest,
  CompareItemsTableSpacerCell,
  CompareItemsTableImageCell,
  CompareItemsTableAddItemImageCell,
  CompareItemsTableItemCell,
  CompareItemsTableSplitSelectorCell,
  CompareItemsTableSplitHeaderCell,
  CompareItemsTableAttributeCell,
  CompareItemsAttribute,
  CompareItemsTableDataCell,
  CompareItemsTableItemIndicatorCell,
  CompareItemsSplitGroup
} from '../types'
import { COLUMN_PADDING, DEFAULT_COLOR_THEME } from '../config/CompareItems_const'

export const TableContext = createContext<CompareItemsTable>(null)

export default function useTable(): CompareItemsTable {
  const table = useContext(TableContext)
  return table
}

function cellWidth(cols: number, index: number, attributeColWidth: number, itemColWidth: number): number {
  return 0 === index
    ? attributeColWidth + COLUMN_PADDING + cellWidth(cols - 1, 1, attributeColWidth, itemColWidth)
    : cols * itemColWidth + (cols - 1) * COLUMN_PADDING
}

export function useTableState(
  props: CompareItemsProps,
  {
    layout,
    selectedItems,
    images,
    sigtest
  }: {
    layout: CompareItemsLayout
    modes: CompareItemsModes
    selectedItems: CompareItemsSelectedItems
    images: CompareItemsImages
    sigtest: CompareItemsSigtest
  }
): CompareItemsTable {
  const { attributeColWidth, itemColWidth } = layout

  const table: CompareItemsTable = useMemo(() => {
    const table: CompareItemsTable = {
      attributeColFullWidth: attributeColWidth + COLUMN_PADDING,
      itemColFullWidth: itemColWidth + COLUMN_PADDING,
      columnPadding: COLUMN_PADDING,
      canAddItems: false !== props.canAddItems && null == props.splitBy?.ref,
      canChangeItems: false !== props.canChangeItems && null == props.splitBy?.ref,
      canRemoveItems: false !== props.canRemoveItems && null == props.splitBy?.ref,
      itemName: props.itemName,
      rows: [],
      dataRowsStartIndex: 1
    }

    const selectedSplitGroup: CompareItemsSplitGroup =
      null != props.splitBy.ref ? props.splitGroups?.find(group => group.id === props.splitBy.ref) : null
    const splitByValues = null != props.splitBy?.ref && 'values' === props.splitBy?.group

    // indicators row
    const indicatorsRow = addIndicatorsRow({
      selectedItems,
      attributeColWidth,
      itemColWidth,
      sortByAttributeIndex: selectedItems.sortByAttributeIndex,
      canRemoveItems: table.canRemoveItems,
      itemName: props.itemName,
      colorTheme: props.colorTheme || DEFAULT_COLOR_THEME,
      selectedSplitGroup,
      splitByValues,
      splitBy: props.splitBy
    })
    table.dataRowsStartIndex++

    // images
    const imagesRow = images.showImages
      ? addImagesRow({
          images,
          attributeColWidth,
          itemColWidth,
          selectedSplitGroup,
          splitByValues,
          splitBy: props.splitBy,
          canAddItems: table.canAddItems
        })
      : null
    if (imagesRow) {
      table.dataRowsStartIndex++
    }

    // items
    const itemsRow = addItemsRow({
      items: props.items,
      selectedItems,
      itemName: props.itemName,
      attributeColWidth,
      itemColWidth,
      selectedSplitGroup,
      splitByValues,
      splitBy: props.splitBy,
      canAddItems: table.canAddItems,
      canChangeItems: table.canChangeItems
    })

    // split headers
    const splitHeadersRow =
      null == props.splitBy?.ref
        ? null
        : addSplitHeadersRow({
            selectedItems,
            selectedSplitGroup,
            splitByValues,
            splitBy: props.splitBy,
            attributeColWidth,
            itemColWidth
          })

    if (splitHeadersRow) {
      table.dataRowsStartIndex++
    }

    // split selector
    const splitSelectorRow = props.splitGroups?.length
      ? addSplitSelectorRow({ splitGroups: props.splitGroups, splitBy: props.splitBy, attributeColWidth, itemColWidth })
      : null
    if (splitSelectorRow) {
      table.dataRowsStartIndex++
    }

    // data
    const dataRows = addDataRows({
      attributes: props.attributes,
      selectedItems,
      selectedSplitGroup,
      splitByValues,
      splitBy: props.splitBy,
      attributeColWidth,
      itemColWidth,
      useSigtext: 'main' === props.analysisMode,
      useSigtest: props.enableSigtestAnalysis && 'sigtest' === props.analysisMode,
      sigtest,
      percentDecimals: props.percentDecimals
    })

    table.rows.push(
      indicatorsRow,
      imagesRow,
      ...(null != props.splitBy?.ref && 'values' === props.splitBy.group
        ? [splitHeadersRow, itemsRow]
        : [itemsRow, splitHeadersRow]),
      splitSelectorRow,
      ...dataRows
    )
    table.rows = table.rows.filter(row => null != row)

    return table
  }, [
    images,
    props.items,
    selectedItems,
    props.itemName,
    props.attributes,
    props.splitGroups,
    props.splitBy,
    attributeColWidth,
    itemColWidth,
    props.canAddItems,
    props.canChangeItems,
    props.canRemoveItems,
    props.colorTheme,
    props.enableSigtestAnalysis,
    props.analysisMode,
    sigtest,
    props.percentDecimals
  ])

  return table
}

function addIndicatorsRow({
  selectedItems,
  attributeColWidth,
  itemColWidth,
  sortByAttributeIndex,
  canRemoveItems,
  itemName,
  colorTheme,
  selectedSplitGroup,
  splitByValues,
  splitBy
}: {
  selectedItems: CompareItemsSelectedItems
  attributeColWidth: number
  itemColWidth: number
  sortByAttributeIndex: number
  canRemoveItems: boolean
  itemName: string
  colorTheme: CompareItemsProps['colorTheme']
  selectedSplitGroup: CompareItemsSplitGroup
  splitByValues: boolean
  splitBy: CompareItemsProps['splitBy']
}): CompareItemsTable['rows'][number] {
  const row: CompareItemsTable['rows'][number] = {
    key: 'indicators',
    seamless: true,
    cells: []
  }

  const cols =
    null == splitBy?.ref || null == selectedSplitGroup?.values || splitByValues ? 1 : selectedSplitGroup.values.length

  const spacerCell: CompareItemsTableSpacerCell = {
    type: 'spacer' as const,
    key: 'spacer',
    sticky: true,
    width: cellWidth(1, 0, attributeColWidth, itemColWidth),
    col: 0,
    height: 13,
    zIndex: 20
  }
  row.cells.push(spacerCell)

  // indicator cells
  const indicatorCells: CompareItemsTableItemIndicatorCell[] = []
  selectedItems.items.forEach((item, itemIndex) => {
    const cell: CompareItemsTableItemIndicatorCell = {
      type: 'indicator' as const,
      key: `indicator-${itemIndex}-${item._index}`,
      index: itemIndex,
      rank: null,
      canRemove: canRemoveItems,
      itemName: itemName,
      width: cellWidth(cols, itemIndex + 1, attributeColWidth, itemColWidth),
      col: cols * itemIndex + 1
    }
    if (null != sortByAttributeIndex && 0 <= sortByAttributeIndex) {
      const rank = item.attributes[sortByAttributeIndex]?.rank ?? null
      cell.rank = rank
      cell.color = null != rank ? colorTheme?.[rank % colorTheme.length] : ''
    }
    indicatorCells.push(cell)
  })

  row.cells.push(...indicatorCells)

  if (splitByValues && selectedSplitGroup) {
    // add indicators for repeated additional items
    for (let i = 1; i < selectedSplitGroup.values.length; i++) {
      row.cells.push(
        ...indicatorCells.map((cell, cellIndex) => {
          return {
            ...cell,
            key: `${cell.key}-${i}`,
            col: selectedItems.items.length * i + cell.col,
            width: cellWidth(cols, i * selectedSplitGroup.values.length + cellIndex, attributeColWidth, itemColWidth)
          }
        })
      )
    }
  }

  return row
}

function addImagesRow({
  images,
  attributeColWidth,
  itemColWidth,
  selectedSplitGroup,
  splitByValues,
  splitBy,
  canAddItems
}: {
  images: CompareItemsImages
  attributeColWidth: number
  itemColWidth: number
  selectedSplitGroup: CompareItemsSplitGroup
  splitByValues: boolean
  splitBy: CompareItemsProps['splitBy']
  canAddItems: boolean
}): CompareItemsTable['rows'][number] {
  const spacerCell: CompareItemsTableSpacerCell = {
    type: 'spacer' as const,
    key: 'spacer',
    sticky: true,
    width: cellWidth(1, 0, attributeColWidth, itemColWidth),
    col: 0
  }

  const cols =
    null == splitBy?.ref || null == selectedSplitGroup?.values || splitByValues ? 1 : selectedSplitGroup.values.length

  const imageCells: CompareItemsTableImageCell[] = images.selectedImages.map((image, imageIndex) => {
    return {
      type: 'image' as const,
      key: `image-${imageIndex}-${image.id}`,
      image,
      width: cellWidth(cols, imageIndex + 1, attributeColWidth, itemColWidth),
      col: cols * imageIndex + 1,
      imageWidth: itemColWidth
    }
  })

  const row: CompareItemsTable['rows'][number] = {
    key: 'images',
    seamless: true,
    cells: [spacerCell, ...imageCells]
  }

  if (splitByValues && selectedSplitGroup) {
    // add images for repeated additional items
    for (let i = 1; i < selectedSplitGroup.values.length; i++) {
      row.cells.push(
        ...imageCells.map((cell, cellIndex) => {
          return {
            ...cell,
            key: `${cell.key}-${i}`,
            col: images.selectedImages.length * i + cell.col,
            width: cellWidth(cols, i * selectedSplitGroup.values.length + cellIndex, attributeColWidth, itemColWidth)
          }
        })
      )
    }
  }

  if (canAddItems) {
    const addItemImageCell: CompareItemsTableAddItemImageCell = {
      type: 'add_item_image' as const,
      key: 'add_item_image' as const,
      width: cellWidth(1, images.selectedImages.length + 1, attributeColWidth, itemColWidth),
      col: images.selectedImages.length + 1
    }
    row.cells.push(addItemImageCell)
  }

  return row
}

function addItemsRow({
  items,
  selectedItems,
  itemName = 'item',
  attributeColWidth,
  itemColWidth,
  selectedSplitGroup,
  splitByValues,
  splitBy,
  canAddItems,
  canChangeItems
}: {
  items: CompareItemsProps['items']
  selectedItems: CompareItemsSelectedItems
  itemName?: string
  attributeColWidth: number
  itemColWidth: number
  selectedSplitGroup: CompareItemsSplitGroup
  splitByValues: boolean
  splitBy: CompareItemsProps['splitBy']
  canAddItems: boolean
  canChangeItems: boolean
}): CompareItemsTable['rows'][number] {
  const spacerCell: CompareItemsTableSpacerCell = {
    type: 'spacer' as const,
    key: 'spacer',
    sticky: true,
    width: cellWidth(1, 0, attributeColWidth, itemColWidth),
    col: 0
  }

  const cols =
    null == splitBy?.ref || null == selectedSplitGroup?.values || splitByValues ? 1 : selectedSplitGroup.values.length

  const itemCells: CompareItemsTableItemCell[] = selectedItems.items.map((item, itemIndex) => {
    const cell: CompareItemsTableItemCell = {
      type: 'item' as const,
      key: `item-${itemIndex}-${item._index}`,
      itemName: itemName,
      item,
      selectedItemIndex: itemIndex,
      width: cellWidth(cols, itemIndex + 1, attributeColWidth, itemColWidth),
      col: cols * itemIndex + 1,
      lastInGroup: true
    }
    if (canChangeItems) {
      cell.canChange = true
      cell.items = items
    }
    if (splitByValues) {
      cell.lastInGroup = itemIndex === selectedItems.items.length - 1
      cell.subGroup = true
    } else if (null != splitBy?.ref) {
      cell.splitSwap = true
    }
    return cell
  })

  const row: CompareItemsTable['rows'][number] = {
    key: 'items',
    seamless: Boolean(selectedSplitGroup && !splitByValues),
    cells: [spacerCell, ...itemCells]
  }

  if (splitByValues && selectedSplitGroup) {
    // add items for repeated additional items
    for (let i = 1; i < selectedSplitGroup.values.length; i++) {
      row.cells.push(
        ...itemCells.map((cell, cellIndex) => {
          return {
            ...cell,
            key: `${cell.key}-${i}`,
            col: selectedItems.items.length * i + cell.col,
            width: cellWidth(cols, i * selectedSplitGroup.values.length + cellIndex, attributeColWidth, itemColWidth)
          }
        })
      )
    }
  }

  if (canAddItems) {
    row.cells.push({
      type: 'add_item' as const,
      key: 'add_item' as const,
      width: cellWidth(1, selectedItems.items.length + 1, attributeColWidth, itemColWidth),
      col: selectedItems.items.length + 1,
      items,
      itemName
    })
  }

  return row
}

function addSplitHeadersRow({
  selectedItems,
  selectedSplitGroup,
  splitByValues,
  attributeColWidth,
  itemColWidth
}: {
  selectedItems: CompareItemsSelectedItems
  selectedSplitGroup: CompareItemsSplitGroup
  splitByValues: boolean
  splitBy: CompareItemsProps['splitBy']
  attributeColWidth: number
  itemColWidth: number
}) {
  const row: CompareItemsTable['rows'][number] = { key: 'splitHeader', cells: [], seamless: splitByValues }

  const spacerCell: CompareItemsTableSpacerCell = {
    type: 'spacer' as const,
    key: 'spacer',
    sticky: true,
    width: cellWidth(1, 0, attributeColWidth, itemColWidth),
    col: 0,
    zIndex: 20
  }
  row.cells.push(spacerCell)

  const headerCells: CompareItemsTableSplitHeaderCell[] = []
  if (selectedSplitGroup) {
    if (splitByValues) {
      if (selectedItems.items.length) {
        selectedSplitGroup.values?.forEach((value, valueIndex) => {
          const headerCell: CompareItemsTableSplitHeaderCell = {
            type: 'split_header',
            key: `split-header-${valueIndex}-${value.id}`,
            text: value.name,
            width: cellWidth(
              selectedItems.items.length,
              1 + valueIndex * selectedItems.items.length,
              attributeColWidth,
              itemColWidth
            ),
            col: 1 + valueIndex * selectedItems.items.length,
            valueIndex: valueIndex,
            lastInGroup: true,
            splitSwap: true
          }
          headerCells.push(headerCell)
        })
      }
    } else {
      selectedItems.items.forEach((_item, itemIndex) => {
        selectedSplitGroup.values?.forEach((value, valueIndex) => {
          const headerCell: CompareItemsTableSplitHeaderCell = {
            type: 'split_header',
            key: `split-header-${itemIndex}-${valueIndex}-${value.id}`,
            text: value.name,
            width: cellWidth(1, itemIndex + 1, attributeColWidth, itemColWidth),
            col: itemIndex + 1,
            valueIndex: valueIndex,
            lastInGroup: false,
            subGroup: true
          }
          if (valueIndex === selectedSplitGroup.values.length - 1) {
            headerCell.lastInGroup = true
          }
          headerCells.push(headerCell)
        })
      })
    }
  }
  row.cells.push(...headerCells)

  return row
}

function addSplitSelectorRow({
  splitGroups,
  splitBy,
  attributeColWidth,
  itemColWidth
}: {
  splitGroups: CompareItemsProps['splitGroups']
  splitBy: CompareItemsProps['splitBy']
  attributeColWidth: number
  itemColWidth: number
}): CompareItemsTable['rows'][number] {
  const splitSelectorCell: CompareItemsTableSplitSelectorCell = {
    type: 'split_selector',
    key: 'split_selector',
    sticky: true,
    splitGroups,
    splitBy,
    width: cellWidth(1, 0, attributeColWidth, itemColWidth),
    col: 0
  }
  return { key: 'splitSelector', seamless: true, cells: [splitSelectorCell] }
}

function formatPercents(value: number, percentDecimals?: number): string {
  if (null == percentDecimals) {
    // integers without decimals, non integer values round to 2 decimals
    return Number.isInteger(value) ? value.toString() : isNaN(value) ? '' : value.toFixed(2)
  } else {
    // format to fixed decimals with trailing zeros if needed
    return value.toFixed(percentDecimals)
  }
}

function formatAmount(value: number): string {
  // integers without decimals, non integer values round to 1 decimals
  return Number.isInteger(value) ? value.toString() : isNaN(value) ? '' : value.toFixed(1)
}

function dataCellText(
  itemAttribute: CompareItemsItem['attributes'][number],
  options?: { useSigtext?: boolean; percentDecimals?: number }
): string {
  const { useSigtext = false, percentDecimals } = options || {}
  return itemAttribute?.text
    ? `${itemAttribute.text}${
        useSigtext && itemAttribute?.sigtext
          ? `<b class="${itemAttribute.sigtext.class}">${itemAttribute.sigtext.text}</b>`
          : ''
      }`
    : null != itemAttribute?.percents
    ? `${formatPercents(Number(itemAttribute.percents), percentDecimals)}%${
        useSigtext && itemAttribute?.sigtext
          ? `<b class="${itemAttribute.sigtext.class}">${itemAttribute.sigtext.text}</b>`
          : ''
      }`
    : null != itemAttribute?.amount
    ? `${formatAmount(Number(itemAttribute.amount))}${
        useSigtext && itemAttribute?.sigtext
          ? `<b class="${itemAttribute.sigtext.class}">${itemAttribute.sigtext.text}</b>`
          : ''
      }`
    : null != itemAttribute?.total
    ? `${itemAttribute.total}`
    : ''
}

function addDataRows({
  attributes,
  selectedItems,
  selectedSplitGroup,
  splitByValues,
  splitBy,
  attributeColWidth,
  itemColWidth,
  useSigtext,
  useSigtest,
  sigtest,
  percentDecimals
}: {
  attributes: CompareItemsAttribute[]
  selectedItems: CompareItemsSelectedItems
  selectedSplitGroup: CompareItemsSplitGroup
  splitByValues: boolean
  splitBy: CompareItemsProps['splitBy']
  attributeColWidth: number
  itemColWidth: number
  useSigtext: boolean
  useSigtest: boolean
  sigtest: CompareItemsSigtest
  percentDecimals: number
}): CompareItemsTable['rows'] {
  const rows: CompareItemsTable['rows'] = []

  let attributePosition = 0
  attributes.forEach((attribute, attributeIndex) => {
    if (attribute.hidden) {
      return
    }

    const row: CompareItemsTable['rows'][number] = {
      key: `attribute-${attribute.position}-${attributeIndex}`,
      cells: []
    }

    const hasPosition = 'number' === typeof attribute.position
    if (hasPosition) {
      attributePosition++
    }

    const attributeCell: CompareItemsTableAttributeCell = {
      type: 'attribute',
      key: `attribute-${attribute.position}-${attributeIndex}`,
      text: decodeText(attribute.text).replace(/<\/?[biu]>/gi, ''),
      attribute,
      position: hasPosition ? attributePosition : null,
      attributeIndex,
      sortable: false !== attribute.sortable,
      sorted: attributeIndex === selectedItems.sortByAttributeIndex ? (selectedItems.reverseSortOrder ? 1 : -1) : null,
      sticky: true,
      width: cellWidth(1, 0, attributeColWidth, itemColWidth),
      col: 0
    }
    row.cells.push(attributeCell)

    const dataCells: CompareItemsTableDataCell[] = Array.from({
      length:
        selectedItems.items.length * (null != splitBy.ref && selectedSplitGroup ? selectedSplitGroup.values.length : 1)
    })

    selectedItems.items.forEach((selectedItem, selectedItemIndex) => {
      const itemAttribute = selectedItem.attributes[attributeIndex]

      if (selectedSplitGroup) {
        selectedSplitGroup.values.forEach((value, valueIndex) => {
          const itemSplitAttribute = (
            itemAttribute as CompareItemsItem['attributes'][number] & {
              [groupID: string]: { [key: string]: CompareItemsItem['attributes'][number] }
            }
          )?.[selectedSplitGroup.id]?.[value.id]
          const dataCell: CompareItemsTableDataCell = {
            type: 'data',
            key: `data-${attributeIndex}-${selectedItemIndex}-${valueIndex}`,
            text: dataCellText(itemSplitAttribute, { useSigtext, percentDecimals }),
            bold: Boolean(attribute.info),
            width: cellWidth(1, selectedItemIndex + 1, attributeColWidth, itemColWidth),
            col: splitByValues
              ? 1 + selectedItemIndex + valueIndex * selectedItems.items.length
              : 1 + selectedItemIndex * selectedSplitGroup.values.length + valueIndex
          }

          if (useSigtest && sigtest?.letters?.size) {
            const groupValueSigtest: CompareItemsItem['attributes'][number]['sigtest'] = (
              itemAttribute as CompareItemsItem['attributes'][number] & {
                [groupID: string]: {
                  [valueID: string]: {
                    sigtest: CompareItemsItem['attributes'][number]['sigtest']
                  }
                }
              }
            )?.[selectedSplitGroup.id]?.[value.id]?.sigtest
            if (groupValueSigtest?.length) {
              if ('splitValue' === sigtest.type) {
                dataCell.sigtest = groupValueSigtest
                  .map(sigtestValue => {
                    let letter = sigtest.letters.get(sigtestValue.i)
                    if (!letter) {
                      return null
                    }
                    const sigtestSplitValue = sigtest.items.get(letter) as CompareItemsSplitGroup['values'][number]
                    if (1 === sigtestValue.l) {
                      letter = letter.toLowerCase()
                    } else if (2 === sigtestValue.l) {
                      letter = letter.toUpperCase()
                    }
                    return {
                      letter,
                      title: `${letter}: ${sigtestSplitValue?.name}`
                    }
                  })
                  .filter(sigtestValue => Boolean(sigtestValue))
              } else if ('item' === sigtest.type) {
                dataCell.sigtest = groupValueSigtest
                  .map(sigtestValue => {
                    let letter = sigtest.letters.get(sigtestValue.i)
                    if (!letter) {
                      return null
                    }
                    const sigtestItem = sigtest.items.get(letter) as CompareItemsItem
                    if (1 === sigtestValue.l) {
                      letter = letter.toLowerCase()
                    } else if (2 === sigtestValue.l) {
                      letter = letter.toUpperCase()
                    }
                    return {
                      letter,
                      title: `${letter}: ${sigtestItem?.text}`
                    }
                  })
                  .filter(sigtestValue => Boolean(sigtestValue))
              }
            }
          }

          dataCells[
            splitByValues
              ? valueIndex * selectedItems.items.length + selectedItemIndex
              : selectedItemIndex * selectedSplitGroup.values.length + valueIndex
          ] = dataCell
        })
      } else {
        const text = dataCellText(itemAttribute, { useSigtext, percentDecimals })

        const dataCell: CompareItemsTableDataCell = {
          type: 'data',
          key: `data-${attributeIndex}-${selectedItemIndex}`,
          text,
          bold: Boolean(attribute.info),
          width: cellWidth(1, selectedItemIndex + 1, attributeColWidth, itemColWidth),
          col: selectedItemIndex + 1
        }

        if (useSigtest && sigtest?.letters?.size && itemAttribute?.sigtest?.length) {
          dataCell.sigtest = itemAttribute.sigtest
            .map(attributeSigtest => {
              let letter = sigtest.letters.get(attributeSigtest.i)
              if (!letter) {
                return null
              }
              const sigtestItem = sigtest.items.get(letter) as CompareItemsItem
              if (1 === attributeSigtest.l) {
                letter = letter.toLowerCase()
              } else if (2 === attributeSigtest.l) {
                letter = letter.toUpperCase()
              }
              return {
                letter,
                title: `${letter}: ${sigtestItem?.text}`
              }
            })
            .filter(sigtestValue => Boolean(sigtestValue))
        }

        dataCells[selectedItemIndex] = dataCell
      }
    })

    row.cells.push(...dataCells)

    rows.push(row)
  })

  return rows
}
